<template>
  <div class="creation-select-container">
    <DropdownSelect
        :label="label"
        :modelValue="modelValue"
        :options="creationOptions"
        :maxTextLength="maxTextLength"
        :loading="loading"
        @update:modelValue="handleSelectionChange"
    />

    <DateFilter
        v-if="showDateFilter"
        :visible="showDateFilter"
        :on-filter="handleDateFilterApply"
        @click.stop
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DropdownSelect from './DropdownSelect.vue';
import DateFilter from '@/components/table-filters/DateFilter.vue';
import reportsService from '@/service/reportsService';
import { useRouter, useRoute } from 'vue-router';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  modelValue: {
    type: [String, Number],
    default: 'all'
  },
  maxTextLength: {
    type: Number,
    default: 15
  },
  tabType: {
    type: String,
    default: 'test'
  }
});

const emit = defineEmits(['update:modelValue', 'optionsLoaded', 'filterChange']);

const creationOptions = ref([]);
const loading = ref(false);
const rawCreationData = ref([]);
const showDateFilter = ref(false);

const formatDate = (date) => {
  return date.toISOString().split('T')[0];
};

const getDateRanges = () => {
  const now = new Date();
  const ranges = {};

  ranges.today = {
    start: formatDate(now),
    end: formatDate(now)
  };

  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7);
  ranges.week = {
    start: formatDate(weekAgo),
    end: formatDate(now)
  };

  const monthAgo = new Date(now);
  monthAgo.setMonth(now.getMonth() - 1);
  ranges.month = {
    start: formatDate(monthAgo),
    end: formatDate(now)
  };

  const threeMonthsAgo = new Date(now);
  threeMonthsAgo.setMonth(now.getMonth() - 3);
  ranges.quarter = {
    start: formatDate(threeMonthsAgo),
    end: formatDate(now)
  };

  const yearAgo = new Date(now);
  yearAgo.setFullYear(now.getFullYear() - 1);
  ranges.year = {
    start: formatDate(yearAgo),
    end: formatDate(now)
  };

  return ranges;
};

const processCreationRanges = (data) => {
  const ranges = [
    { value: 'all', label: t('allTime') },
    { value: 'custom', label: t('date_range_selected') }
  ];

  return ranges;
};

const fetchCreationData = async () => {
  loading.value = true;

  try {
    const apiTypeMapping = {
      test: 'selfAssessments',
      program: 'programs',
      gameLabs: 'gameLabs',
      user: 'users'
    };

    const apiType = apiTypeMapping[props.tabType] || 'selfAssessments';

    const params = {
      type: apiType,
      parameterType: 'properties',
      status: 1,
      page: 1,
      limit: 100
    };

    const response = await reportsService.getReportData(params);

    if (response.data && response.data.data) {
      rawCreationData.value = response.data.data;
      const ranges = processCreationRanges(response.data.data);
      creationOptions.value = ranges;
      emit('optionsLoaded', ranges);
    } else {
      creationOptions.value = [
        { value: 'all', label: t('allTime') },
        { value: 'custom', label: t('date_range_selected') }
      ];
    }

  } catch (error) {
    creationOptions.value = [
      { value: 'all', label: t('allTime') },
      { value: 'custom', label: t('date_range_selected') }
    ];

    emit('optionsLoaded', creationOptions.value);

  } finally {
    loading.value = false;
  }
};

const formatDateForBackend = (dateString) => {
  if (!dateString) return undefined;
  const [day, month, year] = dateString.split('-');
  return `${year}-${month}-${day}`;
};

const handleSelectionChange = async (selectedValue) => {
  console.log('📅 Selection Changed:', selectedValue);

  if (selectedValue === 'custom') {
    showDateFilter.value = true;
    return;
  }

  if (selectedValue === 'all') {
    // ÖNCE emit'leri yap
    emit('update:modelValue', selectedValue);
    emit('filterChange', {
      creation: null,
      selectedRange: selectedValue
    });

    console.log('📅 Emitted "all" filter');

    // SONRA router.push yap
    const newQuery = { ...route.query };
    delete newQuery['created_at[start]'];
    delete newQuery['created_at[end]'];

    try {
      await router.push({ query: newQuery });
      console.log('📅 Router cleared successfully');
    } catch (error) {
      console.error('📅 Router clear error:', error);
    }
  }
};

const handleDateFilterApply = async (filters) => {
  showDateFilter.value = false;

  const formattedStartDate = formatDateForBackend(filters.startDate);
  const formattedEndDate = formatDateForBackend(filters.endDate);

  console.log('📅 Date Filter Applied:', filters);
  console.log('📅 Formatted Start:', formattedStartDate);
  console.log('📅 Formatted End:', formattedEndDate);

  const creationParams = {
    start: formattedStartDate,
    end: formattedEndDate
  };

  // ÖNCE emit'leri yap
  emit('update:modelValue', 'custom');
  emit('filterChange', {
    creation: creationParams,
    selectedRange: 'custom'
  });

  console.log('📅 Emitted filterChange:', creationParams);

  // SONRA router.push yap (await kullan)
  try {
    await router.push({
      query: {
        ...route.query,
        'created_at[start]': formattedStartDate,
        'created_at[end]': formattedEndDate
      }
    });
    console.log('📅 Router updated successfully');
  } catch (error) {
    console.error('📅 Router update error:', error);
  }
};

watch(() => props.tabType, async (newTabType) => {
  if (newTabType) {
    await fetchCreationData();
  }
});

onMounted(async () => {
  await fetchCreationData();
});

defineExpose({
  refreshData: fetchCreationData,
  getCreationData: () => rawCreationData.value
});
</script>

<style scoped>
.creation-select-container {
  position: relative;
}
</style>
